import { useState, useEffect, useCallback } from "react";
import type { SrRoute } from "./types";
import { SR_NAV } from "./types";

const VALID = new Set<SrRoute>(SR_NAV.map((n) => n.id));
// Pages that take a sub-path: #ai-sessions/<sessionId> opens one session.
const WITH_PARAM = new Set<SrRoute>(["ai-sessions"]);
// Convex ids are alphanumeric; anything else is not an id.
const PARAM_RE = /^[A-Za-z0-9_-]{1,64}$/;

export interface SrLocation {
  route: SrRoute;
  param: string | null;
}

export function parseSrHash(hash: string): SrLocation {
  const raw = hash.replace(/^#/, "");
  const slash = raw.indexOf("/");
  const id = (slash === -1 ? raw : raw.slice(0, slash)) as SrRoute;
  if (!VALID.has(id)) return { route: "overview", param: null };
  const rest = slash === -1 ? "" : raw.slice(slash + 1);
  return { route: id, param: WITH_PARAM.has(id) && PARAM_RE.test(rest) ? rest : null };
}

export function useSrRouteHash(): [SrRoute, (r: SrRoute, param?: string) => void, string | null] {
  const [loc, setLoc] = useState<SrLocation>(() => parseSrHash(window.location.hash));

  useEffect(() => {
    const onHashChange = () => setLoc(parseSrHash(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const navigate = useCallback((r: SrRoute, param?: string) => {
    window.location.hash = "#" + r + (param ? `/${param}` : "");
  }, []);

  return [loc.route, navigate, loc.param];
}
